import { useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  AlertTriangle, Wrench, Hammer, BellRing, Moon, Building2, CheckCircle2,
  Package, FileText, Megaphone, Loader2, Receipt, Sparkles, SquarePen, type LucideIcon,
} from "lucide-react";
import { useDueFollowups } from "@/features/followups/followups";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { OncallTodayChip } from "@/features/team/OncallChip";
import { TriageStream, useTriageRows, type TriageKind } from "./TriageStream";
import { PartsPickupPanel } from "@/features/parts/PartsPickupPanel";
import { MaterialRequestsQueue } from "@/features/jobs/MaterialRequestsQueue";
import { ProposalsPanel, useOpenProposals } from "./ProposalsPanel";
// PROPERTY-LINK-01: new accounts at addresses Sedgwick already knows — one question each.
import { HouseHandoffPanel, useHouseHandoffs } from "./HouseHandoffPanel";
import { InstallsTodayCard } from "./InstallsTodayCard";
import { OncallOvernightPanel } from "./OncallOvernightPanel";
import { CustomerRequestsPanel, useNewRequests } from "./CustomerRequestsPanel";
import { AppointmentChangesPanel, useApptChanges } from "./AppointmentChangesPanel";
import { UnsentInvoicesPanel } from "./UnsentInvoicesPanel";
import { CloseoutNudgesPanel, useCloseoutNudges } from "./CloseoutNudgesPanel";
import { FieldCashPanel } from "./FieldCashPanel";
import { useFieldCash } from "@/features/jobs/fieldPay";
import { useJobs, useJobQueues } from "@/features/jobs/jobs";
import { useSubInvoices } from "@/features/jobs/subinvoices";
import { useMaterialRequestQueue } from "@/features/jobs/materialRequests";
import { useUnsentBillableInvoices } from "@/features/comms/invoiceSend";
import { WeatherInline } from "./WeatherOutlook";
import { AlertBand, useAutoToday, autoTodayBits } from "./TodayExtras";
import { useTimeOffRequests } from "@/features/team/team";
import { useNavigateWithBack } from "@/lib/navBack";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/cn";
import { Tabs } from "@/components/ui/Tabs";

/**
 * Today v5 — THE DESK (TODAY-REDESIGN-02; Seth picked Exhibit A from the
 * 2026-10-13 mockups: "Today tab is very cluttered and long now").
 *
 * The everything-feed (8 sections in one scroll, 5 notice strips, 7 tinted
 * jump-scroll tiles) became one screen:
 *   · a compact one-row header (date · on-call pair · weather),
 *   · ONE AlertBand — stuck items, coverage gaps, late POs — absent when quiet,
 *   · the pulse as a CALM scoreboard: neutral tiles, zero renders gray, amber
 *     only on counts that demand a human today; a tile click SELECTS the
 *     matching queue tab (nothing scrolls),
 *   · the eight sections as QUEUES under the shared 360 tab bar, counts on
 *     the tabs (amber = something hard-broken inside), one panel visible,
 *     empty queues say "All clear" instead of rendering scaffolding,
 *   · the auto-ran + renewals lines as ONE quiet footer,
 *   · keyboard: 1–8 select queues, ←/→ step — a dispatcher lives here all day.
 */

/* ───────────────────────────── the queues ───────────────────────────── */

// TODAY-ORDER-02 (Seth 2026-10-22: "Parts & Service seem more important to display first
// than Board"): the queues in the order the office works them — Parts and Service lead.
const QUEUE_KEYS = ["parts", "service", "board", "billing", "proposals", "installs", "followups", "oncall"] as const;
type QueueKey = (typeof QUEUE_KEYS)[number];

const QUEUE_META: Record<QueueKey, { label: string; icon: LucideIcon }> = {
  board: { label: "Board", icon: AlertTriangle },
  parts: { label: "Parts", icon: Package },
  service: { label: "Service", icon: Wrench },
  billing: { label: "Billing", icon: Receipt },
  proposals: { label: "Proposals", icon: FileText },
  installs: { label: "Installs", icon: Hammer },
  followups: { label: "Follow-Ups", icon: BellRing },
  oncall: { label: "After Hours", icon: Moon },
};

const BOARD_KINDS: TriageKind[] = ["conflict", "bounce", "mismatch", "noemail", "looseend"];

/** Honest tab counts: each queue counts THE SAME rows its panel renders —
 *  the triage slices reuse useTriageRows (same queries, deduped), the rest
 *  reuse each panel's own hook. A count may lag a beat behind the panel
 *  (both settle from the same cache), but it can never disagree with it. */
function useQueueCounts() {
  const triage = useTriageRows();
  const matReqs = useMaterialRequestQueue(null).data?.requests;
  const newReqs = useNewRequests().data;
  const appts = useApptChanges().data;
  const fieldCash = useFieldCash().data;
  const unsent = useUnsentBillableInvoices(30).data;
  const closeouts = useCloseoutNudges().data;
  const proposals = useOpenProposals().data;
  const handoffs = useHouseHandoffs().data;
  const { data: jobs } = useJobs();
  const { data: jobQueues } = useJobQueues(jobs);
  const subInvoices = useSubInvoices("review").data;
  const oncallWaiting = useTodayPulse().oncall;

  const by = (kinds: TriageKind[]) => triage.rows.filter((r) => kinds.includes(r.kind));
  const boardRows = by(BOARD_KINDS);
  const partsRows = by(["parts"]);
  const t = (v: number) => (triage.loading ? null : v);

  const counts: Record<QueueKey, number | null> = {
    board: t(boardRows.length),
    parts: t(partsRows.length + (matReqs?.filter((r) => r.status !== "cancelled").length ?? 0)),
    service: t(by(["newticket"]).length + (newReqs?.length ?? 0) + (appts?.length ?? 0) + (handoffs?.length ?? 0)),
    billing:
      fieldCash === undefined && unsent === undefined && closeouts === undefined
        ? null
        : (fieldCash?.length ?? 0) + (unsent?.length ?? 0) + (closeouts?.length ?? 0),
    proposals: proposals === undefined ? null : proposals.length,
    installs: jobQueues
      ? jobQueues.needsSchedule.size + jobQueues.needsPermit.size + jobQueues.readyToClose.size + (subInvoices?.length ?? 0)
      : null,
    followups: t(by(["fu_manual"]).length),
    oncall: oncallWaiting,
  };
  // Amber = something inside is hard-broken or someone is literally waiting.
  const hot: Record<QueueKey, boolean> = {
    board: boardRows.some((r) => r.tone === "danger"),
    parts: partsRows.some((r) => r.tone === "danger"),
    service: false,
    billing: false,
    proposals: false,
    installs: false,
    followups: false,
    oncall: oncallWaiting > 0,
  };
  return { counts, hot };
}

/* ───────────────────────────── the pulse ───────────────────────────── */

interface Pulse {
  oncall: number;
  partsOrder: number;
  partsArrived: number;
  newRequests: number;
  timeOff: number;
}

function useTodayPulse(): Pulse {
  const timeOff = useTimeOffRequests().data?.length ?? 0;
  const q = useQuery({
    queryKey: ["today_pulse"],
    refetchInterval: 60 * 1000,
    queryFn: async (): Promise<Omit<Pulse, "timeOff">> => {
      const { data } = await supabase
        .from("pegasus_dispatch")
        .select("lane_key, tags, part_state, status, created_at")
        .eq("status", "pending_schedule")
        .is("deleted_at", null)
        .limit(500);
      const rows = (data ?? []) as { lane_key: string; tags: string[] | null; part_state: string | null; created_at: string }[];
      const dayAgo = Date.now() - 24 * 3600000;
      let oncall = 0, partsOrder = 0, partsArrived = 0, newRequests = 0;
      for (const r of rows) {
        const tags = r.tags ?? [];
        if (tags.includes("oncall") && tags.includes("needs_triage")) oncall++;
        else if (r.lane_key === "order_parts" && (r.part_state === "needs_ordering" || r.part_state === "backordered")) partsOrder++;
        else if (r.lane_key === "order_parts" && r.part_state === "arrived") partsArrived++;
        else if (r.lane_key === "incoming" && new Date(r.created_at).getTime() > dayAgo) newRequests++;
      }
      return { oncall, partsOrder, partsArrived, newRequests };
    },
  });
  return { oncall: 0, partsOrder: 0, partsArrived: 0, newRequests: 0, ...(q.data ?? {}), timeOff };
}

/* ── BULLETIN-01 (Seth 2026-09-01): "a bulletin message for the day that
   admin can push." ONE message per day per AUDIENCE (BULLETIN-02, Seth
   2026-09-30: "one that just office users could see as well — if Brent's
   truck is down we don't need all the techs seeing that"):
     'team'   → this bar AND every tech's phone (unchanged behavior)
     'office' → this bar only; the phones filter it out
   Upsert replaces; clearing deletes the row. TODAY-REDESIGN-02b (Seth
   2026-10-13: "I liked the bulletin button… you didn't build it. We'll need
   something that posts to techs and then office"): the way in is mock A's
   "✎ Bulletin" BUTTON in the header — it opens ONE composer with the
   audience as a toggle inside (techs' phones / office only), defaulting to
   whichever is unposted next: techs first, then the office note. ───────── */

const chicagoToday = () =>
  new Date().toLocaleDateString("en-CA", { timeZone: "America/Chicago" });

type BulletinAudience = "team" | "office";

interface DayBulletin {
  id: string;
  day: string;
  body: string;
  audience: BulletinAudience;
  created_by: string | null;
  updated_at: string;
}

function useDayBulletins() {
  return useQuery({
    queryKey: ["day_bulletin", chicagoToday()],
    refetchInterval: 5 * 60 * 1000,
    queryFn: async (): Promise<{ team: DayBulletin | null; office: DayBulletin | null }> => {
      const { data, error } = await supabase
        .from("pegasus_bulletin")
        .select("*")
        .eq("day", chicagoToday());
      if (error) throw error;
      const rows = (data ?? []) as DayBulletin[];
      return {
        team: rows.find((r) => r.audience !== "office") ?? null,
        office: rows.find((r) => r.audience === "office") ?? null,
      };
    },
  });
}

/** "sethsimonson@…" → "Sethsimonson" is ugly; prefer the part before @ with
 *  dots/underscores as spaces, title-cased. Good enough for a byline. */
function bylineName(email: string | null): string {
  if (!email) return "the office";
  const local = email.split("@")[0].replace(/[._-]+/g, " ");
  return local.replace(/\b\w/g, (c) => c.toUpperCase());
}

function BulletinBar({ composeSignal = 0 }: { composeSignal?: number }) {
  const canPost = useCan("team.bulletin");
  const qc = useQueryClient();
  const { data, isLoading } = useDayBulletins();
  const [editing, setEditing] = useState<BulletinAudience | null>(null);
  const [draft, setDraft] = useState("");
  // What the textarea was last pre-filled with — "has the user typed
  // anything of their own?" is draft !== lastPrefill.
  const lastPrefill = useRef("");

  const bulletin = data?.team ?? null;
  const office = data?.office ?? null;

  const open = (aud: BulletinAudience) => {
    const pre = (aud === "office" ? office?.body : bulletin?.body) ?? "";
    setDraft(pre);
    lastPrefill.current = pre;
    setEditing(aud);
  };
  // Toggling the audience mid-compose keeps TYPED text (they're choosing
  // where to send it) but swaps untouched prefills, so flipping to an
  // audience that already has a post edits it instead of blind-overwriting.
  const switchAud = (aud: BulletinAudience) => {
    if (aud === editing) return;
    setDraft((d) => {
      if (d.trim() === "" || d === lastPrefill.current) {
        const pre = (aud === "office" ? office?.body : bulletin?.body) ?? "";
        lastPrefill.current = pre;
        return pre;
      }
      return d;
    });
    setEditing(aud);
  };
  const close = () => {
    setEditing(null);
    setDraft("");
    lastPrefill.current = "";
  };

  // The header's "✎ Bulletin" button lives in TodayView (so typing in the
  // textarea never re-renders the whole Desk); each bump of composeSignal
  // opens the composer on whichever audience is UNPOSTED next — techs
  // first, then the office note; both posted = edit the team one (the
  // posted cards carry their own Edit as well).
  const seenSignal = useRef(0);
  useEffect(() => {
    if (composeSignal === seenSignal.current) return;
    seenSignal.current = composeSignal;
    // Already composing? Don't clobber what they've typed — the composer is
    // open right below the button they just clicked.
    if (editing !== null) return;
    open(!bulletin ? "team" : !office ? "office" : "team");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composeSignal]);

  const save = useMutation({
    mutationFn: async (v: { audience: BulletinAudience; body: string }) => {
      const { data: u } = await supabase.auth.getUser();
      const existing = v.audience === "office" ? data?.office : data?.team;
      const { error } = await supabase.from("pegasus_bulletin").upsert(
        {
          day: chicagoToday(),
          audience: v.audience,
          body: v.body.trim(),
          created_by: existing?.created_by ?? u?.user?.email ?? null,
          updated_by: u?.user?.email ?? null,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "day,audience" },
      );
      if (error) throw error;
    },
    onSuccess: () => {
      close();
      void qc.invalidateQueries({ queryKey: ["day_bulletin"] });
    },
  });
  const clear = useMutation({
    mutationFn: async (audience: BulletinAudience) => {
      const { error } = await supabase
        .from("pegasus_bulletin")
        .delete()
        .eq("day", chicagoToday())
        .eq("audience", audience);
      if (error) throw error;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["day_bulletin"] }),
  });

  if (isLoading) return null;

  // ── the composer: ONE editor, the audience is a toggle INSIDE it — the
  //    mock A flow ("post to techs, and then office", same door). The panel
  //    recolors with the audience so there's never a doubt where it goes.
  const segBtn = "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-semibold transition-colors";
  const existingBody = editing === "office" ? office?.body : bulletin?.body;
  const editor = editing !== null && (
    <div className={cn(
      "rounded-2xl border p-4 shadow-sm",
      editing === "team" ? "border-amber-300/70 bg-amber-50/70" : "border-slate-300/80 bg-slate-100/70",
    )}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <span className="inline-flex items-center rounded-full bg-white/80 p-0.5 ring-1 ring-black/[0.06]">
          <button
            type="button"
            onClick={() => switchAud("team")}
            title="Post to the whole crew — shows here and on every tech's phone"
            className={cn(segBtn, editing === "team" ? "bg-amber-100 text-amber-900" : "text-slate-500 hover:text-slate-700")}
          >
            <Megaphone className="h-3.5 w-3.5" />
            Every Tech&apos;s Phone
          </button>
          <button
            type="button"
            onClick={() => switchAud("office")}
            title="Office note — desktop only, techs never see it"
            className={cn(segBtn, editing === "office" ? "bg-slate-200 text-slate-800" : "text-slate-500 hover:text-slate-700")}
          >
            <Building2 className="h-3.5 w-3.5" />
            Office Only
          </button>
        </span>
        <span className="text-[12px] font-medium text-slate-500">
          {editing === "team" ? "goes on every tech's phone today" : "desktop only — techs never see it"}
        </span>
      </div>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        rows={2}
        autoFocus
        placeholder={editing === "team"
          ? 'e.g. "Shop meeting at 4 — everyone back by 3:45" or "Filters truck arrives Thursday, hold non-urgent stock runs"'
          : 'e.g. "Brent\'s truck is in the shop — he\'s in Wayne\'s rig today" or "Phones heavy till noon, Holly out"'}
        className={cn(
          "mt-2 w-full rounded-xl border bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2",
          editing === "team"
            ? "border-amber-200 focus:border-amber-400 focus:ring-amber-400/25"
            : "border-slate-200 focus:border-slate-400 focus:ring-slate-400/25",
        )}
      />
      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          disabled={!draft.trim() || save.isPending}
          onClick={() => editing && save.mutate({ audience: editing, body: draft })}
          className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-3.5 py-1.5 text-xs font-bold text-white hover:bg-slate-800 disabled:opacity-40"
        >
          {save.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
          {existingBody ? "Update It" : "Post It"}
        </button>
        <button type="button" onClick={close} className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-slate-500 hover:bg-white">
          Cancel
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-2 empty:hidden">
      {/* ── the TEAM bulletin (phones + here); editing it swaps the card for
            the composer in place ── */}
      {editing !== "team" && bulletin ? (
        <div className="flex items-start gap-3 rounded-2xl border border-amber-300/70 bg-amber-50/70 px-4 py-3 shadow-sm">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700">
            <Megaphone className="h-[18px] w-[18px]" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-extrabold uppercase tracking-wider text-amber-700/80">Today&apos;s Bulletin — on every tech&apos;s phone</div>
            <p className="mt-0.5 whitespace-pre-wrap text-[14.5px] font-semibold leading-snug text-slate-900">{bulletin.body}</p>
            <div className="mt-0.5 text-[11px] text-slate-500">— {bylineName(bulletin.created_by)}</div>
          </div>
          {canPost && (
            <span className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                onClick={() => open("team")}
                className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-100"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => clear.mutate("team")}
                title="Take today's bulletin down"
                className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-slate-400 hover:bg-amber-100 hover:text-slate-600"
              >
                Clear
              </button>
            </span>
          )}
        </div>
      ) : null}

      {/* ── the OFFICE-ONLY note (BULLETIN-02) — never reaches a phone ── */}
      {editing !== "office" && office ? (
        <div className="flex items-start gap-3 rounded-2xl border border-slate-300/70 bg-slate-100/60 px-4 py-3 shadow-sm">
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-slate-200 text-slate-600">
            <Building2 className="h-[18px] w-[18px]" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-extrabold uppercase tracking-wider text-slate-500">Office Only — techs don&apos;t see this</div>
            <p className="mt-0.5 whitespace-pre-wrap text-[14.5px] font-semibold leading-snug text-slate-900">{office.body}</p>
            <div className="mt-0.5 text-[11px] text-slate-500">— {bylineName(office.created_by)}</div>
          </div>
          {canPost && (
            <span className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                onClick={() => open("office")}
                className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-200"
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => clear.mutate("office")}
                title="Take the office note down"
                className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-slate-400 hover:bg-slate-200 hover:text-slate-600"
              >
                Clear
              </button>
            </span>
          )}
        </div>
      ) : null}

      {/* ── the composer, opened by the header's ✎ Bulletin button (or a
            card's Edit); when nothing is posted and nothing is being
            composed, this whole block renders NOTHING — the header button
            is the only affordance. ── */}
      {editor}
    </div>
  );
}

/* ─────────────────────────── the scoreboard ─────────────────────────── */

/** DESK tile: a number and a label, block-stacked. Neutral by design — zero
 *  renders gray, amber ONLY on counts that demand a human today. Clicking
 *  SELECTS the matching queue tab (jump-scrolling is gone with the scroll). */
function StatTile({ n, label, sub, hot, onClick }: {
  n: number;
  label: string;
  sub: string;
  hot?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={n === 0 ? `${label} — all clear` : `${label} — ${sub}`}
      className="rounded-xl border border-hairline bg-white px-3.5 py-2 text-left shadow-sm transition-colors hover:border-brand-200 hover:bg-brand-50/40"
    >
      <span
        className={cn(
          "block text-[19px] font-extrabold leading-6 tabular-nums",
          n === 0 ? "text-slate-300" : hot ? "text-amber-600" : "text-slate-900",
        )}
      >
        {n}
      </span>
      <span className="block whitespace-nowrap text-[11px] font-semibold text-slate-500">{label}</span>
    </button>
  );
}

/* ─────────────────────────── queue panel shell ─────────────────────────── */

/** Every child self-hides on a quiet day (returns null), so an empty queue's
 *  content div is :empty — and the proud All-clear line shows instead. Same
 *  CSS trick the old sections used to vanish, pointed the other way. */
function QueuePanel({ k, children }: { k: QueueKey; children: React.ReactNode }) {
  return (
    <div className="group/q">
      <div data-queue-content className="space-y-3">{children}</div>
      <div className="hidden items-center justify-center gap-2.5 rounded-2xl border border-hairline bg-white px-5 py-10 shadow-sm group-has-[[data-queue-content]:empty]/q:flex">
        <CheckCircle2 className="h-5 w-5 text-emerald-500" />
        <span className="text-sm font-semibold text-slate-500">All clear — nothing waiting in {QUEUE_META[k].label}.</span>
      </div>
    </div>
  );
}

const QUEUE_BODY: Record<QueueKey, React.ReactNode> = {
  board: <TriageStream include={BOARD_KINDS} />,
  parts: (
    <>
      <TriageStream include={["parts"]} />
      {/* MATERIAL-REQ-01: what install / new-construction crews asked for from their phones. */}
      <MaterialRequestsQueue hideWhenEmpty />
      <div className="rounded-2xl border border-hairline bg-white p-4 shadow-sm">
        <PartsPickupPanel mode="today" />
      </div>
    </>
  ),
  service: (
    <>
      <TriageStream include={["newticket"]} />
      <CustomerRequestsPanel />
      <AppointmentChangesPanel />
      {/* PROPERTY-LINK-01: a new account at a known address — same house changing hands? */}
      <HouseHandoffPanel />
    </>
  ),
  billing: (
    <>
      <FieldCashPanel />
      <UnsentInvoicesPanel />
      {/* NUDGE-01: install close-outs the office hasn't sent 5+ days on. */}
      <CloseoutNudgesPanel />
    </>
  ),
  proposals: <ProposalsPanel />,
  installs: <InstallsTodayCard />,
  followups: <TriageStream include={["fu_manual"]} />,
  oncall: <OncallOvernightPanel />,
};

/* ─────────────────────────── the Desk itself ─────────────────────────── */

export function TodayView() {
  const today = format(new Date(), "EEEE, MMMM d");
  const pulse = useTodayPulse();
  const navigate = useNavigateWithBack();
  const { counts, hot } = useQueueCounts();
  // Mock A's header button: bumping the signal opens BulletinBar's composer
  // (techs' phones first, then the office note — see BulletinBar).
  const canPostBulletin = useCan("team.bulletin");
  const [bulletinSignal, setBulletinSignal] = useState(0);

  const [tab, setTab] = useState<QueueKey>(QUEUE_KEYS[0]);
  // TAB-SPEED: a queue mounts on first visit and STAYS mounted (hidden), so
  // flipping back is instant and in-panel state (peeks, filters) survives.
  const [visited, setVisited] = useState<Set<QueueKey>>(() => new Set([QUEUE_KEYS[0] as QueueKey]));
  const select = (k: QueueKey) => {
    setTab(k);
    setVisited((prev) => (prev.has(k) ? prev : new Set(prev).add(k)));
  };

  // Keyboard: 1–8 select queues, ←/→ step — never while typing somewhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.isContentEditable)) return;
      const n = parseInt(e.key, 10);
      if (n >= 1 && n <= QUEUE_KEYS.length) {
        select(QUEUE_KEYS[n - 1]);
        return;
      }
      if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
        const i = QUEUE_KEYS.indexOf(tab);
        const next = (i + (e.key === "ArrowRight" ? 1 : QUEUE_KEYS.length - 1)) % QUEUE_KEYS.length;
        select(QUEUE_KEYS[next]);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // TODAY-AFTER-HOURS-DUP (Seth 2026-10-22, "trim to the tab"): a tile earns its place
  // only when it says something the tab count can't — the Parts split and Time Off
  // (no tab). After-Hours, New Requests, Follow-Ups and Invoices To Send repeated their
  // tab exactly and are gone; the tabs carry the counts (amber when hot).
  const stats: { label: string; sub: string; n: number; hot?: boolean; go: () => void }[] = [
    { label: "Parts To Order", sub: "tickets waiting on a PO", n: pulse.partsOrder, hot: true, go: () => select("parts") },
    { label: "Parts In — Book It", sub: "arrived, ready to schedule", n: pulse.partsArrived, go: () => select("parts") },
    { label: "Time Off Requests", sub: "techs waiting on a yes/no", n: pulse.timeOff, hot: true, go: () => navigate("/inbox?tab=requests") },
  ];

  return (
    <div className="mx-auto max-w-7xl space-y-4 p-6 lg:px-8">
      {/* ── ONE header row: date is the headline; on-call pair and the
            forecast are day context riding beside it (TODAY-V5b kept). ── */}
      <header className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="min-w-0">
          <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-slate-400">
            Today &middot; Command Center
          </p>
          <h2 className="text-[22px] font-extrabold leading-7 tracking-tight text-slate-900">{today}</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2 pt-3">
          <OncallTodayChip />
          <WeatherInline />
        </div>
        {canPostBulletin && (
          <div className="ml-auto flex items-center pt-3">
            <button
              type="button"
              onClick={() => setBulletinSignal((n) => n + 1)}
              title="Post the day's bulletin — the techs' phones first, then an office-only note"
              className="inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg border border-hairline bg-white px-3 text-[12.5px] font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50"
            >
              <SquarePen className="h-4 w-4 text-slate-400" />
              Bulletin
            </button>
          </div>
        )}
      </header>

      {/* ── the day's messages (posted bulletin cards + the composer) ── */}
      <BulletinBar composeSignal={bulletinSignal} />

      {/* ── ONE alert slot — absent on a clean morning ── */}
      <AlertBand selectTab={(k) => select(k as QueueKey)} />

      {/* ── the calm scoreboard — tiles SELECT queues, nothing scrolls ── */}
      <div className="flex flex-wrap items-stretch gap-2">
        {stats.map((s) => (
          <StatTile key={s.label} n={s.n} label={s.label} sub={s.sub} hot={s.hot} onClick={s.go} />
        ))}
      </div>

      {/* ── the queues: the 360's tab bar doing the 360's job ── */}
      <div>
        <Tabs
          variant="bar"
          size="sm"
          value={tab}
          onChange={(k) => select(k as QueueKey)}
          items={QUEUE_KEYS.map((k) => ({
            key: k,
            label: QUEUE_META[k].label,
            icon: QUEUE_META[k].icon,
            count: counts[k],
            countTone: hot[k] ? ("amber" as const) : undefined,
          }))}
        />
        <div className="mt-4">
          {QUEUE_KEYS.map((k) =>
            visited.has(k) ? (
              <div key={k} hidden={tab !== k}>
                <QueuePanel k={k}>{QUEUE_BODY[k]}</QueuePanel>
              </div>
            ) : null,
          )}
        </div>
      </div>

      {/* ── one quiet line: what ran itself + the renewals pointer ── */}
      <DeskFooter />
    </div>
  );
}

/** The old AutoTodayStrip + RenewalsStrip as ONE footer line. The renewal
 *  call list itself lives on Memberships (renewal emails go out automatically
 *  30 days ahead — Seth 2026-09-02 demoted the list; the Desk keeps the
 *  count and the pointer). */
function DeskFooter() {
  const bits = autoTodayBits(useAutoToday().data);
  const renewals = (useDueFollowups().data ?? []).filter((f) => f.source === "agreement").length;
  if (bits.length === 0 && renewals === 0) return null;
  return (
    <div
      className="flex flex-wrap items-center gap-x-2.5 gap-y-1 px-1 text-[12.5px] text-slate-500"
      title="These ran themselves — reminders, staged messages, portal self-bookings, cycle counts. Renewal calls live on Memberships."
    >
      <Sparkles className="h-3.5 w-3.5 shrink-0 text-indigo-400" />
      {bits.length > 0 && (
        <>
          <span className="text-[10.5px] font-extrabold uppercase tracking-wider text-slate-400">Ran itself today</span>
          <span className="min-w-0 font-medium">{bits.join(" · ")}</span>
        </>
      )}
      {bits.length > 0 && renewals > 0 && <span className="text-slate-300">·</span>}
      {renewals > 0 && (
        <Link to="/agreements" className="font-semibold text-slate-500 underline-offset-2 hover:text-brand-700 hover:underline">
          Renewals: {renewals} call{renewals === 1 ? "" : "s"} due → Memberships
        </Link>
      )}
    </div>
  );
}
